import { useEffect, useState, useCallback } from "react";
import { Select, Button, Spin } from "antd";
import "./style.scss";
import { useDispatch } from "react-redux";
import { adminLogoutAction } from "store/sagas/adminAuthSaga";
import { AdminLogoutParams } from "types/auth";
import axios from "config/axios";
import constant from "constant/action";
import SkillMapTable from "components/SkillMapTable";

interface MasterData {
  machining_processes: { id: number; process_jpn: string }[];
  users: { id: number; name: string }[];
  product_large_categories: { id: number; name: string }[];
  product_medium_categories: { id: number; name: string }[];
  product_small_categories: { id: number; name: string }[];
  product_detail_categories: { id: number; name: string }[];
}

interface CategorySummaryData {
  process_name?: string;
  user_name?: string;
  header: any[];
  structure: any[];
  data: any[];
}

const SkillMap = () => {
  const dispatch = useDispatch();

  // State management
  const [mode, setMode] = useState<"process" | "user">("process");
  const [masterData, setMasterData] = useState<MasterData | null>(null);
  const [categorySummaryData, setCategorySummaryData] = useState<CategorySummaryData | null>(null);
  const [isLoadingData, setIsLoadingData] = useState<boolean>(false);

  // Filter states
  const [selectedProcessId, setSelectedProcessId] = useState<number | null>(null);
  const [selectedUserId, setSelectedUserId] = useState<number | null>(null);
  const [selectedLargeCategoryId, setSelectedLargeCategoryId] = useState<number | null>(null);
  const [selectedMediumCategoryId, setSelectedMediumCategoryId] = useState<number | null>(null);
  const [selectedSmallCategoryId, setSelectedSmallCategoryId] = useState<number | null>(null);
  const [selectedDetailCategoryId, setSelectedDetailCategoryId] = useState<number | null>(null);

  // Sort state（CSVエクスポート用）
  const [currentSortState, setCurrentSortState] = useState<{
    columnIndex: number | null;
    direction: 'asc' | 'desc' | null;
    targetDetail: {
      product_large_category_id: number | null;
      product_medium_category_id: number | null;
      product_small_category_id: number | null;
      product_detail_category_id: number | null;
    } | null;
  }>({
    columnIndex: null,
    direction: null,
    targetDetail: null
  });


  const handleLogout = () => {
    const data: AdminLogoutParams = {
      logout_type: "primary"
    };
    dispatch(adminLogoutAction(data));
    localStorage.removeItem('selected_process');
    localStorage.removeItem('selected_process_name');
    localStorage.removeItem('cachedProcesses');
  };

  const fetchMasterData = useCallback(async () => {
    try {
      const response = await axios.get(constant.SKILL_MAP_MASTER_DATA);
      if (response && response.data) {
        setMasterData(response.data);
      }
    } catch (error) {
      console.error("Error fetching master data:", error);
    }
  }, []);

  const refreshCategorySummaryData = () => {
    setIsLoadingData(true);
    setCategorySummaryData(null);
  };

  const fetchCategorySummaryData = useCallback(async () => {
    refreshCategorySummaryData();
    // モードに応じて適切なIDをチェック
    const selectedId = mode === "process" ? selectedProcessId : selectedUserId;

    if (!selectedId) {
      // IDが選択されていない場合はデータをクリア
      setCategorySummaryData(null);
      setIsLoadingData(false);
      return;
    }

    try {
      const params: any = {
        summary_type: mode,
        id: selectedId,
      };

      if (selectedLargeCategoryId) {
        params.product_large_category_id = selectedLargeCategoryId;
      }
      if (selectedMediumCategoryId) {
        params.product_medium_category_id = selectedMediumCategoryId;
      }
      if (selectedSmallCategoryId) {
        params.product_small_category_id = selectedSmallCategoryId;
      }
      if (selectedDetailCategoryId) {
        params.product_detail_category_id = selectedDetailCategoryId;
      }

      const response = await axios.get(constant.SKILL_MAP_CATEGORY_SUMMARY, {
        params
      });

      if (response && response.data) {
        setCategorySummaryData(response.data);
      }
    } catch (error) {
      console.error("Error fetching category summary data:", error);
      setCategorySummaryData(null);
    } finally {
      setIsLoadingData(false);
    }
  }, [mode, selectedProcessId, selectedUserId, selectedLargeCategoryId, selectedMediumCategoryId, selectedSmallCategoryId, selectedDetailCategoryId]);

  useEffect(() => {
    fetchMasterData();
  }, [fetchMasterData]);

  useEffect(() => {
    fetchCategorySummaryData();
  }, [fetchCategorySummaryData]);

  const handleModeChange = (newMode: "process" | "user") => {
    refreshCategorySummaryData();
    setMode(newMode);
    // モード変更時はソート状態をリセット
    setCurrentSortState({ columnIndex: null, direction: null, targetDetail: null });
  };

  const handleProcessChange = (value: number) => {
    refreshCategorySummaryData();
    setSelectedProcessId(value);
    // 工程変更時はソート状態をリセット
    setCurrentSortState({ columnIndex: null, direction: null, targetDetail: null });
  };

  const handleUserChange = (value: number) => {
    refreshCategorySummaryData();
    setSelectedUserId(value);
    // ユーザー変更時はソート状態をリセット
    setCurrentSortState({ columnIndex: null, direction: null, targetDetail: null });
  };

  const handleLargeCategoryChange = (value: number | null) => {
    refreshCategorySummaryData();
    setSelectedLargeCategoryId(value);
  };

  const handleMediumCategoryChange = (value: number | null) => {
    refreshCategorySummaryData();
    setSelectedMediumCategoryId(value);
  };

  const handleSmallCategoryChange = (value: number | null) => {
    refreshCategorySummaryData();
    setSelectedSmallCategoryId(value);
  };

  const handleDetailCategoryChange = (value: number | null) => {
    refreshCategorySummaryData();
    setSelectedDetailCategoryId(value);
  };

  // ソート状態の更新ハンドラー
  const handleSortChange = (sortState: {
    columnIndex: number | null;
    direction: 'asc' | 'desc' | null;
    targetDetail: {
      product_large_category_id: number | null;
      product_medium_category_id: number | null;
      product_small_category_id: number | null;
      product_detail_category_id: number | null;
    } | null;
  }) => {
    setCurrentSortState(sortState);
  };

  const handleClearFilters = () => {
    refreshCategorySummaryData();
    setSelectedLargeCategoryId(null);
    setSelectedMediumCategoryId(null);
    setSelectedSmallCategoryId(null);
    setSelectedDetailCategoryId(null);
  };

  const handleExport = async () => {
    // モードに応じて適切なIDをチェック
    const selectedId = mode === "process" ? selectedProcessId : selectedUserId;
    const alertMessage = mode === "process" ? "工程を選択してください。" : "ユーザーを選択してください。";

    if (!selectedId) {
      alert(alertMessage);
      return;
    }

    if (isDataEmpty()) {
      alert("エクスポートするデータがありません。");
      return;
    }

    try {
      const params: any = {
        summary_type: mode,
        id: selectedId,
      };

      if (selectedLargeCategoryId) {
        params.product_large_category_id = selectedLargeCategoryId;
      }
      if (selectedMediumCategoryId) {
        params.product_medium_category_id = selectedMediumCategoryId;
      }
      if (selectedSmallCategoryId) {
        params.product_small_category_id = selectedSmallCategoryId;
      }
      if (selectedDetailCategoryId) {
        params.product_detail_category_id = selectedDetailCategoryId;
      }

      // ソートパラメータを追加（targetDetailを直接使用）
      if (currentSortState.targetDetail && currentSortState.direction) {
        params.sort_column_large_id = currentSortState.targetDetail.product_large_category_id;
        params.sort_column_medium_id = currentSortState.targetDetail.product_medium_category_id;
        params.sort_column_small_id = currentSortState.targetDetail.product_small_category_id;
        params.sort_column_detail_id = currentSortState.targetDetail.product_detail_category_id;
        params.sort_direction = currentSortState.direction;
      }

      const response = await axios.get(constant.SKILL_MAP_CSV_EXPORT, {
        params,
        responseType: 'blob'
      });

      // ダウンロード処理
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;

      const now = new Date();
      const timestamp = now.toISOString().replace(/[-:T]/g, '').substring(0, 15);
      link.setAttribute('download', `skill_map_${timestamp}.csv`);

      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error("CSV export error:", error);
      alert("CSVエクスポートに失敗しました。");
    }
  };

  const isDataEmpty = () => {
    if (!categorySummaryData) return true;
    
    const { header, structure, data } = categorySummaryData;
    
    return (
      !header || 
      !structure || 
      !data || 
      header.length === 0 || 
      structure.length === 0 || 
      data.length === 0
    );
  };

  const renderEmptyDataMessage = () => (
    <div className="empty-data-message">
      <p>該当する機種が存在しません</p>
    </div>
  );

  const renderSelectMessage = () => {
    const message = mode === "process" ? "工程を選択してください" : "ユーザーを選択してください";
    return (
      <div className="empty-data-message">
        <p>{message}</p>
      </div>
    );
  };

  return (
    <div className="adminLayoutContainer">
      <div className="header-admin">
        <div className="header-left">
          <p className="header-logo">F-Factory</p>
          <p className="header-title">スキルマップ</p>
        </div>
        <button onClick={handleLogout} className="returnBtn">
          ログイン画面へ戻る
        </button>
      </div>
      <div className="childrenWrapper">
        <div className="skill-map">
          {/* サマリ対象切り替え */}
          <div className="mode-links">
            <div
              className={`mode-link ${mode === "process" ? "active" : ""}`}
              onClick={() => handleModeChange("process")}
            >
              工程
            </div>
            <div
              className={`mode-link ${mode === "user" ? "active" : ""}`}
              onClick={() => handleModeChange("user")}
            >
              作業者
            </div>
          </div>

          {/* 工程/ユーザー選択 */}
          <div className="process-selection">
            {mode === "process" ? (
              <Select
                style={{ minWidth: 200 }}
                placeholder="工程を選択してください"
                value={selectedProcessId}
                onChange={handleProcessChange}
                options={masterData?.machining_processes.map(item => ({
                  value: item.id,
                  label: item.process_jpn
                })) || []}
              />
            ) : (
              <Select
                style={{ minWidth: 200 }}
                placeholder="ユーザーを選択してください"
                value={selectedUserId}
                onChange={handleUserChange}
                options={masterData?.users.map(item => ({
                  value: item.id,
                  label: item.name
                })) || []}
              />
            )}
            <Button onClick={handleExport} className="export-btn">
              現在の表をエクスポート
            </Button>
          </div>

          {/* 絞り込み */}
          <div className="filter-section">
            <span className="filter-label">絞り込み</span>
            <Select
              placeholder="大項目"
              value={selectedLargeCategoryId}
              onChange={handleLargeCategoryChange}
              allowClear
              options={masterData?.product_large_categories.map(item => ({
                value: item.id,
                label: item.name
              })) || []}
            />
            <Select
              placeholder="中項目"
              value={selectedMediumCategoryId}
              onChange={handleMediumCategoryChange}
              allowClear
              options={masterData?.product_medium_categories.map(item => ({
                value: item.id,
                label: item.name
              })) || []}
            />
            <Select
              placeholder="小項目"
              value={selectedSmallCategoryId}
              onChange={handleSmallCategoryChange}
              allowClear
              options={masterData?.product_small_categories.map(item => ({
                value: item.id,
                label: item.name
              })) || []}
            />
            <Select
              placeholder="細項目"
              value={selectedDetailCategoryId}
              onChange={handleDetailCategoryChange}
              allowClear
              options={masterData?.product_detail_categories.map(item => ({
                value: item.id,
                label: item.name
              })) || []}
            />
            <span className="clear-link" onClick={handleClearFilters}>
              条件をクリア
            </span>
          </div>

          {/* スキルマップ本体 */}
          {(() => {
            const selectedId = mode === "process" ? selectedProcessId : selectedUserId;

            if (!selectedId) {
              return renderSelectMessage();
            }

            // ローディング中の場合
            if (isLoadingData) {
              return (
                <div className="loading-container">
                  <Spin size="large" />
                </div>
              );
            }

            if (isDataEmpty()) {
              return renderEmptyDataMessage();
            }

            return <SkillMapTable data={categorySummaryData!} mode={mode} sortState={currentSortState} onSortChange={handleSortChange} />;
          })()}
        </div>
      </div>
    </div>
  );
};

export default SkillMap;